class ScrollingTextEngine {
  constructor(box, speed, direction) {
    this.box = box;
    this.speed = speed;
    this.direction = direction;
    this._running = false;
    this.lastTime = null;
    const innerElement = box.children[0];
    if (!innerElement) throw new Error('ScrollingText must contain a child element.');
    innerElement.style.position = 'relative';
    this.originalInnerElement = innerElement.cloneNode(true);
    this.position = 0;
    this.boxWidth = this.getWidth(box);
    this.innerWidth = this.getWidth(innerElement);
    this.setUpChildrens();
    this.loopFunc = this.loop.bind(this);
  }

  getWidth(element) {
    const rect = element.getBoundingClientRect();
    return rect.right - rect.left;
  }

  refreshWidths() {
    this.boxWidth = this.getWidth(this.box);
    this.innerWidth = this.getWidth(this.box.children[0]);
  }

  calculateNumElements() {
    return Math.ceil(this.boxWidth / this.innerWidth) + 1;
  }

  setUpChildrens() {
    this.refreshWidths();
    const requiredQty = this.calculateNumElements();
    while (this.box.children.length > requiredQty) {
      this.box.removeChild(this.box.lastChild);
    }
    while (this.box.children.length < requiredQty) {
      this.box.appendChild(this.originalInnerElement.cloneNode(true));
    }
  }

  updateFrame(delta) {
    this.refreshWidths();
    this.setUpChildrens();
    const travelDistance = (this.speed * delta) / 1000;
    this.position += travelDistance;
    if (this.position >= this.innerWidth) {
      this.position = this.position % this.innerWidth;
    }
    this.applyTransform();
  }

  applyTransform() {
    const position = this.direction === 'rtl' ? this.position : -this.position;
    Array.prototype.forEach.call(this.box.children, (el) => {
      el.style.transform = `translateX(${position}px)`;
    });
  }

  loop(now) {
    if (!this._running) return;
    const delta = this.lastTime === null ? 0 : now - this.lastTime;
    this.updateFrame(delta);
    this.lastTime = now;
    window.requestAnimationFrame(this.loopFunc);
  }

  start() {
    if (this._running) return;
    this._running = true;
    this.lastTime = null;
    window.requestAnimationFrame(this.loopFunc);
  }

  stop() {
    this._running = false;
  }
}

class ScrollingText extends HTMLElement {
  constructor() {
    super();
    this._scrollingEngine = null;
    this._windowInFocus = true;
    this._isHovered = false;
    this._isTouching = false;
    this._isDragging = false;
    this._hasDragged = false;
    this._intersectionObserver = null;
    this._touchStartX = 0;
    this._touchStartY = 0;
    this._initialScrollPosition = 0;
    this._mouseStartX = 0;
    this._mouseStartY = 0;
    this._mouseInitialScrollPosition = 0;
    this._dragEndTimeout = null;
    this._pressStartTime = 0;
    this._minClickDelay = 150;
    this._isClickValid = true;
  }

  connectedCallback() {
    const direction = this.dataset.scrollingDirection || 'ltr';
    const baseSpeed = parseInt(this.dataset.scrollingSpeed) || 50;
    const speed = window.innerWidth > 768 ? baseSpeed : baseSpeed / 1.5;

    try {
      this._scrollingEngine = new ScrollingTextEngine(this, speed, direction);
      this.setupIntersectionObserver();
      this.setupWindowEvents();
      this.setupInteractionEvents();
    } catch (e) {
      console.error('Error initializing ScrollingText:', e.message);
    }
  }

  disconnectedCallback() {
    this._scrollingEngine?.stop();
    this._intersectionObserver?.disconnect();
    if (this._dragEndTimeout) clearTimeout(this._dragEndTimeout);
    const isDesktop = window.matchMedia('(hover: hover)').matches;
    if (isDesktop) {
      this.removeEventListener('mouseover', this.handleMouseOver);
      this.removeEventListener('mouseout', this.handleMouseOut);
      this.removeEventListener('mousedown', this.handleMouseDown);
      this.removeEventListener('mousemove', this.handleMouseMove);
      this.removeEventListener('mouseup', this.handleMouseUp);
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);
    }
    this.removeEventListener('touchstart', this.handleTouchStart);
    this.removeEventListener('touchmove', this.handleTouchMove);
    this.removeEventListener('touchend', this.handleTouchEnd);
  }

  shouldStart() {
    return this._windowInFocus && !this._isHovered && !this._isDragging;
  }

  setupWindowEvents() {
    window.addEventListener('blur', () => {
      this._windowInFocus = false;
      this._scrollingEngine.stop();
    });
    window.addEventListener('focus', () => {
      this._windowInFocus = true;
      if (this.shouldStart()) this._scrollingEngine.start();
    });
  }

  setupInteractionEvents() {
    const isDesktop = window.matchMedia('(hover: hover)').matches;
    if (isDesktop) {
      this.handleMouseOver = () => {
        this._isHovered = true;
        this._scrollingEngine.stop();
      };
      this.handleMouseOut = () => {
        this._isHovered = false;
        if (this.shouldStart()) this._scrollingEngine.start();
      };
      this.addEventListener('mouseover', this.handleMouseOver);
      this.addEventListener('mouseout', this.handleMouseOut);
      this.handleMouseDown = this.handleMouseDown.bind(this);
      this.handleMouseMove = this.handleMouseMove.bind(this);
      this.handleMouseUp = this.handleMouseUp.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.addEventListener('mousedown', this.handleMouseDown);
      this.addEventListener('mousemove', this.handleMouseMove);
      this.addEventListener('mouseup', this.handleMouseUp);
      this.addEventListener('click', this.handleClick);
    }

    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.addEventListener('touchstart', this.handleTouchStart);
    this.addEventListener('touchmove', this.handleTouchMove);
    this.addEventListener('touchend', this.handleTouchEnd);
  }

  setupIntersectionObserver() {
    this._intersectionObserver = new IntersectionObserver(
      (entries) => {
        const isVisible = entries[0].isIntersecting;
        if (isVisible) {
          if (this.shouldStart()) this._scrollingEngine.start();
        } else {
          this._scrollingEngine.stop();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );
    this._intersectionObserver.observe(this);
  }

  // ==== TOUCH EVENTS ====
  handleTouchStart(e) {
    this._isTouching = true;
    this._pressStartTime = Date.now();
    this._isClickValid = true;
    const touch = e.touches[0];
    this._touchStartX = touch.clientX;
    this._touchStartY = touch.clientY;
    this._initialScrollPosition = this._scrollingEngine.position;
  }

  handleTouchMove(e) {
    if (!this._isTouching) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - this._touchStartX;
    const deltaY = touch.clientY - this._touchStartY;

    if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
      this._isClickValid = false;
    }

    let newPosition = this._initialScrollPosition - deltaX;
    const innerWidth = this._scrollingEngine.innerWidth;
    if (newPosition < 0) {
      newPosition += innerWidth * Math.ceil(Math.abs(newPosition) / innerWidth);
    } else if (newPosition >= innerWidth) {
      newPosition = newPosition % innerWidth;
    }
    this._scrollingEngine.position = newPosition;
    this._scrollingEngine.applyTransform();
  }

  handleTouchEnd(e) {
    this._isTouching = false;
    if (this._dragEndTimeout) clearTimeout(this._dragEndTimeout);
    this._dragEndTimeout = setTimeout(() => {
      this._hasDragged = false;
    }, 150);
  }

  // ==== MOUSE EVENTS ====
  handleMouseDown(e) {
    e.preventDefault();
    this._isDragging = true;
    this._hasDragged = false;
    this._pressStartTime = Date.now();
    this._isClickValid = true;
    this._scrollingEngine.stop();
    this._mouseStartX = e.clientX;
    this._mouseStartY = e.clientY;
    this._mouseInitialScrollPosition = this._scrollingEngine.position;
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  }

  handleMouseMove(e) {
    if (!this._isDragging) return;
    const mouseCurrentX = e.clientX;
    const mouseCurrentY = e.clientY;
    const deltaX = mouseCurrentX - this._mouseStartX;
    const deltaY = mouseCurrentY - this._mouseStartY;

    if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
      this._isClickValid = false;
      this._hasDragged = true;
    }

    let newPosition = this._mouseInitialScrollPosition - deltaX;
    const innerWidth = this._scrollingEngine.innerWidth;
    if (newPosition < 0) {
      newPosition += innerWidth * Math.ceil(Math.abs(newPosition) / innerWidth);
    } else if (newPosition >= innerWidth) {
      newPosition = newPosition % innerWidth;
    }
    this._scrollingEngine.position = newPosition;
    this._scrollingEngine.applyTransform();
  }

  handleMouseUp(e) {
    if (!this._isDragging) return;
    this._isDragging = false;
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);

    if (this._dragEndTimeout) clearTimeout(this._dragEndTimeout);
    this._dragEndTimeout = setTimeout(() => {
      this._hasDragged = false;
    }, 150);

    if (this.shouldStart()) this._scrollingEngine.start();
  }

  handleClick(e) {
    const clickedElement = e.target;

    const isDirectLinkClick =
      clickedElement.tagName === 'A' ||
      clickedElement.closest('a') ||
      clickedElement.tagName === 'BUTTON' ||
      clickedElement.closest('button') ||
      clickedElement.hasAttribute('href') ||
      clickedElement.onclick;

    if (isDirectLinkClick) {
      if (!this._isClickValid || this._hasDragged || this._isDragging) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      return;
    }

    e.preventDefault();
    e.stopPropagation();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (typeof customElements.get('scrolling-text') === 'undefined') {
    customElements.define('scrolling-text', ScrollingText);
  }
});
